import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AlertTitle,
  LinearProgress,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  PlayArrow as RunIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Psychology as AIIcon,
  Preview as PreviewIcon,
  QueryStats as QueryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { snowflakeApi } from '../utils/api';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const PREDEFINED_STRATEGIES = {
  representative: {
    name: 'Representative Sampling',
    description: 'Samples keywords proportionally across impression ranges using percentiles',
    sql: `WITH keyword_percentiles AS (
  SELECT 
    KEYWORD,
    IMPRESSION,
    CLIENT_NAME,
    NTILE(10) OVER (ORDER BY IMPRESSION DESC) as impression_decile
  FROM TOTALSERP.DISTINCT_LIST 
  WHERE CLIENT_ID = :client_id
)
SELECT * FROM (
  SELECT *, 
    ROW_NUMBER() OVER (PARTITION BY impression_decile ORDER BY RANDOM()) as rn
  FROM keyword_percentiles
) 
WHERE rn <= :per_decile
LIMIT :sample_size`,
    parameters: ['client_id', 'sample_size'],
  },
  highVolume: {
    name: 'High Volume Priority',
    description: 'Prioritizes keywords with highest search volume/impressions',
    sql: `SELECT 
  KEYWORD,
  IMPRESSION,
  CLIENT_NAME
FROM TOTALSERP.DISTINCT_LIST 
WHERE CLIENT_ID = :client_id
  AND IMPRESSION >= :min_impressions
ORDER BY IMPRESSION DESC 
LIMIT :sample_size`,
    parameters: ['client_id', 'min_impressions', 'sample_size'],
  },
  stratified: {
    name: 'Stratified Sampling',
    description: 'Ensures representation from different impression strata',
    sql: `WITH impression_strata AS (
  SELECT 
    KEYWORD,
    IMPRESSION,
    CLIENT_NAME,
    CASE 
      WHEN IMPRESSION >= 1000 THEN 'high'
      WHEN IMPRESSION >= 100 THEN 'medium'
      ELSE 'low'
    END as stratum
  FROM TOTALSERP.DISTINCT_LIST 
  WHERE CLIENT_ID = :client_id
),
stratum_counts AS (
  SELECT stratum, COUNT(*) as total_count
  FROM impression_strata
  GROUP BY stratum
)
SELECT * FROM (
  SELECT 
    i.*,
    ROW_NUMBER() OVER (PARTITION BY i.stratum ORDER BY RANDOM()) as rn,
    ROUND((:sample_size * c.total_count::FLOAT / SUM(c.total_count) OVER())) as stratum_sample_size
  FROM impression_strata i
  JOIN stratum_counts c ON i.stratum = c.stratum
) 
WHERE rn <= stratum_sample_size
LIMIT :sample_size`,
    parameters: ['client_id', 'sample_size'],
  },
  topicBased: {
    name: 'Topic-Based Sampling (Future)',
    description: 'Samples keywords based on topic/subtopic distribution (requires Module 4 data)',
    sql: `-- This query will be available after Module 4 integration
WITH topic_distribution AS (
  SELECT 
    k.KEYWORD,
    k.IMPRESSION,
    m4.TOPIC,
    m4.SUBTOPIC,
    m4.CATEGORY
  FROM TOTALSERP.DISTINCT_LIST k
  JOIN MODULE4_RESULTS m4 ON k.KEYWORD = m4.KEYWORD
  WHERE k.CLIENT_ID = :client_id
)
SELECT * FROM (
  SELECT *,
    ROW_NUMBER() OVER (
      PARTITION BY TOPIC, SUBTOPIC 
      ORDER BY IMPRESSION DESC
    ) as rn
  FROM topic_distribution
)
WHERE rn <= :per_topic_sample
LIMIT :sample_size`,
    parameters: ['client_id', 'sample_size', 'per_topic_sample'],
  },
};

const SCHEMA_PROMPT = `-- TOTALSERP.DISTINCT_LIST Table Schema
-- This table contains unique keywords and their search impression data for each client

CREATE TABLE TOTALSERP.DISTINCT_LIST (
    ID NUMBER(38,0) PRIMARY KEY,           -- Unique identifier for each record
    CLIENT_ID VARCHAR(16777216),           -- Client identifier (e.g., '179', '180')
    CLIENT_NAME VARCHAR(16777216),         -- Client name (e.g., 'Bridgestone', 'Firestone')
    KEYWORD VARCHAR(16777216),             -- Search keyword or phrase
    IMPRESSION NUMBER(38,0)                -- Number of search impressions
);

-- Sample Data:
-- ID    | CLIENT_ID | CLIENT_NAME  | KEYWORD                           | IMPRESSION
-- ------|-----------|--------------|-----------------------------------|------------
-- 1001  | 179       | Bridgestone  | bridgestone tires                 | 45000
-- 1002  | 179       | Bridgestone  | bridgestone near me               | 32000
-- 1003  | 179       | Bridgestone  | bridgestone tire prices           | 18500
-- 1004  | 179       | Bridgestone  | bridgestone vs michelin           | 12000
-- 1005  | 179       | Bridgestone  | bridgestone potenza               | 8500
-- 1006  | 179       | Bridgestone  | bridgestone dueler                | 7200
-- 1007  | 179       | Bridgestone  | bridgestone blizzak               | 5600
-- 1008  | 179       | Bridgestone  | bridgestone ecopia                | 3200
-- 1009  | 179       | Bridgestone  | bridgestone turanza               | 2100
-- 1010  | 179       | Bridgestone  | bridgestone weatherpeak           | 850

-- Common Query Patterns:

-- 1. Simple random sampling:
SELECT * FROM TOTALSERP.DISTINCT_LIST 
WHERE CLIENT_ID = :client_id 
ORDER BY RANDOM() 
LIMIT :sample_size;

-- 2. Weighted sampling by impressions:
SELECT * FROM TOTALSERP.DISTINCT_LIST 
WHERE CLIENT_ID = :client_id 
ORDER BY IMPRESSION DESC 
LIMIT :sample_size;

-- 3. Stratified sampling with CTEs:
WITH impression_buckets AS (
    SELECT *, 
           CASE 
               WHEN IMPRESSION >= 10000 THEN 'high'
               WHEN IMPRESSION >= 1000 THEN 'medium'
               ELSE 'low'
           END as bucket
    FROM TOTALSERP.DISTINCT_LIST 
    WHERE CLIENT_ID = :client_id
)
SELECT * FROM (
    SELECT *, 
           ROW_NUMBER() OVER (PARTITION BY bucket ORDER BY RANDOM()) as rn
    FROM impression_buckets
) 
WHERE rn <= :per_bucket_size;

-- Parameters available:
-- :client_id - The client ID to filter by
-- :sample_size - Total number of keywords to sample
-- :min_impressions - Minimum impression threshold
-- :per_bucket_size - Number of samples per bucket/stratum`;

const SamplingStrategyDemo: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedStrategy, setSelectedStrategy] = useState<string>('');
  const [customQuery, setCustomQuery] = useState('');
  const [clientId, setClientId] = useState('179'); // Default to client with data
  const [sampleSize, setSampleSize] = useState(100);
  const [strategyResults, setStrategyResults] = useState<{[key: string]: any}>({});
  const [loadingStrategy, setLoadingStrategy] = useState<{[key: string]: boolean}>({});
  const [error, setError] = useState<string | null>(null);
  const [copiedText, setCopiedText] = useState<string>('');

  const handleCopySQL = (sql: string, label?: string) => {
    navigator.clipboard.writeText(sql);
    setCopiedText(label || 'SQL');
    setTimeout(() => setCopiedText(''), 2000);
  };

  const handlePreviewQuery = async (query: string, strategyKey: string) => {
    setLoadingStrategy({ ...loadingStrategy, [strategyKey]: true });
    setError(null);
    
    try {
      // Execute the actual query using the new endpoint
      const response = await snowflakeApi.post(
        '/api/v1/keywords/execute-sampling-query',
        {
          query: query,
          client_id: clientId,
          sample_size: sampleSize,
          parameters: {
            min_impressions: 100,
            per_topic_sample: 5,
            per_decile: Math.ceil(sampleSize / 10)
          }
        }
      );
      
      const result = response.data;
      
      if (result.status === 'success' && result.data) {
        const queryResult = result.data;
        
        // Format the keywords for display
        const formattedData = queryResult.keywords.map((kw: any) => ({
          keyword: kw.KEYWORD || kw.keyword,
          impression: kw.IMPRESSION || kw.impression || 0,
          client_name: kw.CLIENT_NAME || kw.client_name || clientId,
          stratum: categorizeImpressionLevel(kw.IMPRESSION || kw.impression || 0)
        }));
        
        // Use the statistics from the query execution
        const stats = {
          total_available: queryResult.total_available || 0,
          sample_size: queryResult.row_count || formattedData.length,
          impression_distribution: queryResult.impression_distribution || {
            high: formattedData.filter((d: any) => d.impression >= 10000).length,
            medium: formattedData.filter((d: any) => d.impression >= 1000 && d.impression < 10000).length,
            low: formattedData.filter((d: any) => d.impression >= 100 && d.impression < 1000).length,
            very_low: formattedData.filter((d: any) => d.impression < 100).length,
          },
          avg_impressions: Math.round(queryResult.avg_impressions || 0),
        };
        
        setStrategyResults({
          ...strategyResults,
          [strategyKey]: {
            data: formattedData.slice(0, 10), // Show first 10 for inline preview
            stats: stats
          }
        });
      } else {
        throw new Error('Unexpected response format');
      }
      
      setLoadingStrategy({ ...loadingStrategy, [strategyKey]: false });
    } catch (err: any) {
      console.error('Query execution error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to execute query. Please check your SQL syntax.');
      setLoadingStrategy({ ...loadingStrategy, [strategyKey]: false });
    }
  };
  
  const categorizeImpressionLevel = (impressions: number): string => {
    if (impressions >= 10000) return 'high';
    if (impressions >= 1000) return 'medium';
    if (impressions >= 100) return 'low';
    return 'very_low';
  };

  const renderResultsVisualization = (results: any) => {
    if (!results) return null;

    const { data, stats } = results;

    return (
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        
        {/* Statistics Summary */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: 'primary.50' }}>
              <Typography variant="caption" color="text.secondary">
                Total Available
              </Typography>
              <Typography variant="h6">
                {stats?.total_available?.toLocaleString() || 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: 'success.50' }}>
              <Typography variant="caption" color="text.secondary">
                Sample Size
              </Typography>
              <Typography variant="h6">
                {stats?.sample_size || 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: 'info.50' }}>
              <Typography variant="caption" color="text.secondary">
                Avg Impressions
              </Typography>
              <Typography variant="h6">
                {stats?.avg_impressions?.toLocaleString() || 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, bgcolor: 'warning.50' }}>
              <Typography variant="caption" color="text.secondary">
                Sampling Rate
              </Typography>
              <Typography variant="h6">
                {stats?.total_available ? 
                  ((stats.sample_size / stats.total_available) * 100).toFixed(1) : 0}%
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Impression Distribution */}
        {stats?.impression_distribution && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Impression Distribution
            </Typography>
            <Box sx={{ display: 'flex', height: 40, borderRadius: 1, overflow: 'hidden' }}>
              <Box 
                sx={{ 
                  bgcolor: 'success.main', 
                  width: `${(stats.impression_distribution.high / stats.sample_size) * 100}%`,
                  minWidth: stats.impression_distribution.high > 0 ? 30 : 0
                }} 
              />
              <Box 
                sx={{ 
                  bgcolor: 'warning.main', 
                  width: `${(stats.impression_distribution.medium / stats.sample_size) * 100}%`,
                  minWidth: stats.impression_distribution.medium > 0 ? 30 : 0
                }} 
              />
              <Box 
                sx={{ 
                  bgcolor: 'info.main', 
                  width: `${(stats.impression_distribution.low / stats.sample_size) * 100}%`,
                  minWidth: stats.impression_distribution.low > 0 ? 30 : 0
                }} 
              />
              <Box 
                sx={{ 
                  bgcolor: 'grey.400', 
                  width: `${(stats.impression_distribution.very_low / stats.sample_size) * 100}%`,
                  minWidth: stats.impression_distribution.very_low > 0 ? 30 : 0
                }} 
              />
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip 
                label={`High (10k+): ${stats.impression_distribution.high}`} 
                color="success" 
                size="small" 
              />
              <Chip 
                label={`Medium (1k-10k): ${stats.impression_distribution.medium}`} 
                color="warning" 
                size="small" 
              />
              <Chip 
                label={`Low (100-1k): ${stats.impression_distribution.low}`} 
                color="info" 
                size="small" 
              />
              <Chip 
                label={`Very Low (<100): ${stats.impression_distribution.very_low}`} 
                color="default" 
                size="small" 
              />
            </Stack>
          </Box>
        )}

        {/* Sample Data Preview */}
        <Typography variant="subtitle2" gutterBottom>
          Sample Preview (First 10 Keywords)
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Keyword</TableCell>
                <TableCell align="right">Impressions</TableCell>
                <TableCell>Stratum</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{row.keyword}</TableCell>
                  <TableCell align="right">{row.impression.toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={row.stratum} 
                      size="small"
                      color={
                        row.stratum === 'high' ? 'success' :
                        row.stratum === 'medium' ? 'warning' :
                        row.stratum === 'low' ? 'info' : 'default'
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        SERP Keyword Sampling Strategy Demo
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Test and explore different keyword sampling strategies for SERP analysis. 
        Compare predefined strategies or create custom queries with the provided schema reference.
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Predefined Strategies" icon={<QueryIcon />} iconPosition="start" />
          <Tab label="Custom SQL" icon={<CodeIcon />} iconPosition="start" />
          <Tab label="Schema Reference" icon={<InfoIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Configuration Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuration
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Client ID"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                helperText="Enter the client ID to test with"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Sample Size"
                value={sampleSize}
                onChange={(e) => setSampleSize(Number(e.target.value))}
                inputProps={{ min: 10, max: 1000 }}
                helperText="Number of keywords to sample"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TabPanel value={activeTab} index={0}>
        {/* Predefined Strategies */}
        <Grid container spacing={3}>
          {Object.entries(PREDEFINED_STRATEGIES).map(([key, strategy]) => (
            <Grid item xs={12} key={key}>
              <Accordion 
                expanded={selectedStrategy === key}
                onChange={() => setSelectedStrategy(key)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      {strategy.name}
                    </Typography>
                    {key === 'topicBased' && (
                      <Chip label="Future" size="small" color="info" />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {strategy.description}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      SQL Query:
                    </Typography>
                    <Paper variant="outlined" sx={{ position: 'relative' }}>
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
                        onClick={() => handleCopySQL(strategy.sql, strategy.name)}
                      >
                        <Tooltip title={copiedText === strategy.name ? 'Copied!' : 'Copy SQL'}>
                          <CopyIcon fontSize="small" />
                        </Tooltip>
                      </IconButton>
                      <SyntaxHighlighter 
                        language="sql" 
                        style={vscDarkPlus}
                        customStyle={{ margin: 0, borderRadius: 4 }}
                      >
                        {strategy.sql}
                      </SyntaxHighlighter>
                    </Paper>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={loadingStrategy[key] ? <CircularProgress size={20} /> : <RunIcon />}
                      onClick={() => handlePreviewQuery(strategy.sql, key)}
                      disabled={key === 'topicBased' || loadingStrategy[key]}
                    >
                      {strategyResults[key] ? 'Refresh Results' : 'Run Query'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setCustomQuery(strategy.sql);
                        setActiveTab(1);
                      }}
                    >
                      Use as Custom Query
                    </Button>
                  </Box>

                  {/* Inline Results Visualization */}
                  {strategyResults[key] && renderResultsVisualization(strategyResults[key])}
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {/* Custom SQL */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Custom SQL Query
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Write your own sampling query. Use parameters like :client_id and :sample_size.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Security Notice</AlertTitle>
              For safety, only SELECT queries are allowed. Queries must:
              <ul style={{ marginTop: 8, marginBottom: 0 }}>
                <li>Start with SELECT or WITH (for CTEs)</li>
                <li>Query from TOTALSERP.DISTINCT_LIST table</li>
                <li>Not contain any data modification commands (INSERT, UPDATE, DELETE, etc.)</li>
                <li>Not use system tables or stored procedures</li>
              </ul>
            </Alert>
            
            <TextField
              fullWidth
              multiline
              rows={15}
              value={customQuery}
              onChange={(e) => setCustomQuery(e.target.value)}
              placeholder="Enter your custom SQL query here..."
              sx={{ fontFamily: 'monospace', mb: 2 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Button
                variant="contained"
                startIcon={loadingStrategy['custom'] ? <CircularProgress size={20} /> : <RunIcon />}
                onClick={() => handlePreviewQuery(customQuery, 'custom')}
                disabled={!customQuery || loadingStrategy['custom']}
              >
                {strategyResults['custom'] ? 'Refresh Results' : 'Run Query'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => handleCopySQL(customQuery, 'Custom Query')}
                disabled={!customQuery}
              >
                Copy SQL
              </Button>
            </Box>

            {/* Inline Results for Custom Query */}
            {strategyResults['custom'] && renderResultsVisualization(strategyResults['custom'])}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {/* Schema Reference */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Table Schema & Query Examples
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use this reference to understand the table structure and build your own sampling queries.
            </Typography>
            
            <Paper variant="outlined" sx={{ position: 'relative' }}>
              <IconButton
                size="small"
                sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
                onClick={() => handleCopySQL(SCHEMA_PROMPT, 'Schema')}
              >
                <Tooltip title={copiedText === 'Schema' ? 'Copied!' : 'Copy Schema'}>
                  <CopyIcon fontSize="small" />
                </Tooltip>
              </IconButton>
              <SyntaxHighlighter 
                language="sql" 
                style={vscDarkPlus}
                customStyle={{ margin: 0, borderRadius: 4, fontSize: '0.875rem' }}
              >
                {SCHEMA_PROMPT}
              </SyntaxHighlighter>
            </Paper>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>Tips for Writing Sampling Queries</AlertTitle>
              <ul style={{ marginBottom: 0 }}>
                <li>Use CTEs (WITH clauses) to create complex sampling logic</li>
                <li>NTILE() function is great for creating equal-sized buckets</li>
                <li>ROW_NUMBER() with PARTITION BY helps sample within groups</li>
                <li>RANDOM() function provides random ordering</li>
                <li>Always use parameterized queries (:client_id, :sample_size) for flexibility</li>
              </ul>
            </Alert>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default SamplingStrategyDemo; 